/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, useEffect, useId } from "react";
import { Tooltip } from "react-tooltip";
import { Control, useController } from "react-hook-form";

import IconMultilevelArrow from "./icon-multilevel-select-arrow.svg?react";
import IconArrow from "./icon-select-arrow.svg?react";

import {
  getParentByValue,
  isAllChildrenSelected,
  isAnyChildrenSelected,
  extractChildValues,
  inputLabelFormatter,
  getItemFromOptions,
} from "./utils";
import { Checkbox, IconClose, IconSearch } from "tvs-unified-lib/dist";
import { MultilevelOptionType } from "../SelectField/FormSelect/types";

type MultilevelSelectType = {
  value?: string[];
  onSelected?: (
    value: string[],
    option?: MultilevelOptionType[],
    selectType?: string
  ) => void;
  options: MultilevelOptionType[];
  isMultiple?: boolean;
  defaultValue?: string[];
  containerClassName?: string;
  placeholder?: string;
  className?: string;
  formLabel?: string;
  formSubLabel?: string;
  inputHelpBlock?: string;
  withSearch?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  defaultOption?: string;
  waitApiSetup?: boolean;
  apiLoaded?: boolean;
  name: string;
  control: Control<any>;
};

const MultilevelSelectField = (props: MultilevelSelectType) => {
  const {
    value: externalValue,
    options,
    onSelected: externalOnSelected,
    isMultiple = false,
    defaultValue = "",
    placeholder = "Select...",
    className = "",
    formLabel,
    formSubLabel,
    inputHelpBlock,
    withSearch = true,
    isRequired = false,
    containerClassName,
    disabled = false,
    defaultOption,
    waitApiSetup = false,
    apiLoaded = false,
    name,
    control,
  } = props;

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: externalValue || [],
  });

  const handleSelected = (
    newValue: string[],
    options?: MultilevelOptionType[],
    selectType?: string
  ) => {
    onChange(newValue);
    if (externalOnSelected) {
      externalOnSelected(newValue, options, selectType);
    }
  };

  const [open, setOpen] = useState<boolean>(false);
  const [currentOptions, setCurrentOptions] = useState<MultilevelOptionType[]>(
    []
  );
  const [searchFilter, setSearchFilter] = useState<string | null>(null);
  const [currentLevel, setCurrentLevel] = useState<number>(0);
  const [isSlidingLeft, setIsSlidingLeft] = useState<boolean>(false);
  const [isSlidingRight, setIsSlidingRight] = useState<boolean>(false);

  const componentId = useId();

  const handleClickOption = (option: MultilevelOptionType) => {
    if (disabled) return;
    setSearchFilter(null);

    if (!isMultiple) {
      if (!option.children || option.children.length === 0) {
        handleSelected([option.value], [option]);
        setOpen(false);
      }
    }

    if (option.isParent !== undefined && option.isParent === true) {
      setIsSlidingLeft(true);
      setTimeout(() => setIsSlidingLeft(false), 200);
      const parentItems: any = getParentByValue(options, option.value);

      if (
        parentItems !== null &&
        parentItems.options !== undefined &&
        Array.isArray(parentItems.options)
      ) {
        let parentOptions = {};
        let newOptions = [...parentItems.options];
        if (
          parentItems.previousItem !== undefined &&
          parentItems.previousItem !== null
        ) {
          parentOptions = {
            ...parentItems.previousItem,
            isParent: true,
          };
          newOptions = [parentOptions, ...parentItems.options];
        }
        setCurrentOptions(newOptions);
        setCurrentLevel(currentLevel - 1);
      } else {
        setCurrentOptions(options);
        setCurrentLevel(0);
      }
    } else if (
      option.children === undefined ||
      (Array.isArray(option.children) && option.children.length <= 0)
    ) {
      setOpen(false);
    } else {
      setIsSlidingRight(true);
      setTimeout(() => setIsSlidingRight(false), 200);
      const newOptions: MultilevelOptionType[] = [
        {
          ...option,
          isParent: true,
        },
        ...option.children,
      ];
      setCurrentLevel(currentLevel + 1);
      setCurrentOptions(newOptions);
    }
  };

  const onMultipleSelected = (e: any, option: MultilevelOptionType) => {
    const isSelected: boolean = e.target.checked;
    let currentValue = [...value];
    const selectedOptions = extractChildValues(option);

    if (isSelected) {
      currentValue = [...currentValue, ...selectedOptions.values];
      handleSelected(currentValue, selectedOptions.options, "select");
    } else {
      currentValue = currentValue.filter((v: string) => {
        return !selectedOptions.values.includes(v);
      });
      handleSelected(currentValue, selectedOptions.options, "deselect");
    }
  };

  const filteredOptions = (data: any, filter: string) => {
    return data.reduce((r: any, o: MultilevelOptionType) => {
      if (o.label.toLowerCase().includes(filter.toLowerCase())) {
        r.push(o);
      }
      const children = filteredOptions(o.children || [], filter);
      if (children.length > 0) {
        children.forEach((child: MultilevelOptionType) => {
          r.push(child);
        });
      }
      return r;
    }, []);
  };

  const onFilterSelect = (filter: string) => {
    setOpen(true);

    if (filter.length <= 0) {
      setCurrentOptions(options);
      return true;
    }

    if (filter.length < 3) return false;
    const optionsFiltered: MultilevelOptionType[] = filteredOptions(
      options,
      filter
    );

    optionsFiltered.sort((a: any, b: any) => (a.label < b.label ? -1 : 1));
    setCurrentOptions(optionsFiltered);
  };

  const multilevelSelectRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: any) => {
    if (
      multilevelSelectRef.current &&
      !multilevelSelectRef.current.contains(event.target)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (Array.isArray(options) && options.length > 0) {
      if (waitApiSetup && !apiLoaded) {
        return;
      }

      setCurrentOptions(options);

      setTimeout(() => {
        if (
          Array.isArray(defaultValue) &&
          defaultValue.length > 0 &&
          !isMultiple
        ) {
          const selectedOption = getItemFromOptions(defaultValue[0], options);
          if (selectedOption && selectedOption !== null) {
            handleClickOption(selectedOption);
          }
        }
      }, 300);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(options)]);

  useEffect(() => {
    const handleOutsideClick = handleClickOutside;
    if (open) document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [open]);

  return (
    <div
      className={`theme-multilevel-select-container ${
        containerClassName !== undefined ? containerClassName : ""
      } ${open && "is-opened"} ${disabled ? "is-disabled" : ""}`}
      ref={multilevelSelectRef}
    >
      {formLabel && (
        <label className="theme-form-input-label" htmlFor={`field-${name}`}>
          {formLabel} {isRequired && <span>*</span>}{" "}
        </label>
      )}
      {formSubLabel && (
        <label className="theme-form-sublabel">{formSubLabel}</label>
      )}
      <div
        className="theme-multilevel-select-wrapper"
        onClick={() => {
          if (!disabled) {
            setOpen(!open);
          }
        }}
      >
        <div className="theme-select-icon-arrow">
          <IconArrow />
        </div>
        <input
          disabled={disabled}
          readOnly={true}
          placeholder={placeholder}
          type={"text"}
          value={inputLabelFormatter(value, options)}
          onClick={() => setOpen(!open)}
          className={`theme-form-control ${
            className !== undefined ? className : ""
          } ${isMultiple ? "theme-multilevel-select-multiple-input" : ""}`}
          autoComplete="off"
          onChange={(e) => e.preventDefault()}
        />
        {isMultiple && Array.isArray(value) && value.length > 2 && (
          <>
            <span
              className="theme-multilevel-select-bubble"
              data-tooltip-id={`${componentId}-bubble-tooltip`}
              data-tooltip-delay-hide={500}
            >
              {value.length < 100 ? value.length : "99+"}
            </span>
            <Tooltip
              id={`${componentId}-bubble-tooltip`}
              className="theme-tooltip"
            />
          </>
        )}
      </div>
      {error && <span className="theme-form-error">{error.message}</span>}
      {open && (
        <div
          className={`theme-multilevel-select-list ${
            isSlidingLeft ? "animate-slide-left" : ""
          } ${isSlidingRight ? "animate-slide-right" : ""}`}
        >
          {withSearch && (
            <div className="theme-multilevel-select-option theme-multilevel-select-search">
              <input
                type="text"
                value={searchFilter !== null ? searchFilter : ""}
                className="theme-form-control"
                placeholder="Search"
                autoComplete="off"
                onChange={(e) => {
                  setSearchFilter(e.target.value);
                  onFilterSelect(e.target.value);
                }}
              />
              {searchFilter === "" ||
                (searchFilter === null && (
                  <IconSearch className="theme-multilevel-search-icon" />
                ))}
              {searchFilter !== "" && searchFilter !== null && (
                <IconClose
                  className="theme-multilevel-search-icon"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSearchFilter(null);
                    setCurrentOptions(options);
                  }}
                />
              )}
            </div>
          )}
          {currentOptions.length <= 0 && (
            <div className="theme-multilevel-select-option">
              <span>No value available</span>
            </div>
          )}
          <div
            className={`theme-multilevel-select-option`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentLevel(0);
              setCurrentOptions(options);
              handleSelected(
                defaultOption !== undefined ? [defaultOption] : []
              );
            }}
          >
            <span className="color-theme-red-500">Reset Selection</span>
          </div>
          {currentOptions.map((option: MultilevelOptionType, index: number) => {
            if (option.value !== "" && option.label !== "") {
              const isSelected: boolean =
                value?.includes(option.value) ||
                isAnyChildrenSelected(option, value);
              return (
                <div
                  key={`option-${option.value}-${index}`}
                  className={`theme-multilevel-select-option ${
                    isSelected &&
                    !isMultiple &&
                    "theme-multilevel-select-option-selected"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isMultiple || option.isParent) {
                      handleClickOption(option);
                    }
                  }}
                >
                  {option.isParent && (
                    <div className="theme-multilevel-arrow-left">
                      <IconMultilevelArrow />
                    </div>
                  )}
                  {!option.isParent && isMultiple && (
                    <Checkbox
                      containerClassName="theme-multilevel-cb-container"
                      name={`cb-${option.value}`}
                      value={isSelected ? option.value : ";"}
                      useSecondaryCheckmark={
                        option.children === undefined ||
                        option.children.length < 0
                          ? false
                          : !isAllChildrenSelected(option, value)
                      }
                      onInputChange={(e) => onMultipleSelected(e, option)}
                      checkboxValues={[
                        {
                          label: option.label,
                          value: isSelected ? option.value : "",
                        },
                      ]}
                    />
                  )}
                  {!isMultiple || (isMultiple && option.isParent) ? (
                    <span
                      className={
                        (option.isParent === undefined || !option.isParent) &&
                        currentLevel > 0
                          ? "theme-multilevel-select-option-child"
                          : ""
                      }
                    >
                      {option.label}
                    </span>
                  ) : (
                    <></>
                  )}
                  {option.isParent === undefined &&
                    option.children !== undefined &&
                    Array.isArray(option.children) &&
                    option.children.length > 0 && (
                      <div
                        className="theme-multilevel-arrow-right"
                        onClick={() => {
                          if (isMultiple) {
                            handleClickOption(option);
                          }
                        }}
                      >
                        <IconMultilevelArrow />
                      </div>
                    )}
                </div>
              );
            }
          })}
        </div>
      )}
      {inputHelpBlock && (
        <span className="theme-form-helpblock">{inputHelpBlock}</span>
      )}
    </div>
  );
};

export default MultilevelSelectField;
